/**
 * Loan chat API — wraps the `/chat` router on the eastview-os backend
 * (frontend_documentation/loan-chat.md).
 *
 * Real-time team chat per loan, separate from the formal comment thread. Messages
 * are persisted in Mongo (`loan_processing.loan_chat_messages`) AND broadcast to a
 * `chat:{dealId}` Web PubSub group. History is durable, so the GET works even while
 * Web PubSub is unprovisioned (join/leave 503 in that case — see RealtimeProvider).
 *
 * `{dealId}` is the loan `_id`. Access is gated server-side by the same loan-view
 * rule as presence (admin / permittedUser / role-on-loan / originator-match): a 403
 * means the caller can't see that loan.
 */
import { losHttp } from "../lib/losHttp";

const params = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== "" && v != null) out[k] = v;
  });
  return out;
};

export const loanChatApi = {
  // GET /chat/{dealId}/messages?channel=&limit=&before_id=
  // → { dealId, channel, messages: [...] } (oldest-first; page back with before_id).
  getMessages: (dealId, { channel = "general", limit = 100, before_id } = {}) =>
    losHttp
      .get(`/chat/${dealId}/messages`, {
        params: params({ channel, limit, before_id }),
        // Polled on an interval — keep it out of the console groups.
        noLog: true,
      })
      .then((r) => r.data),

  // POST /chat/{dealId}/messages { htmlString, channel?, mentions? }
  // → { status: "sent", message: {...} } (persist + broadcast).
  // `noLog`: keep the chat send + its return out of the styled console groups.
  sendMessage: (dealId, { htmlString, channel = "general", mentions } = {}) =>
    losHttp
      .post(`/chat/${dealId}/messages`, params({ htmlString, channel, mentions }), {
        noLog: true,
      })
      .then((r) => r.data),

  // POST /chat/{dealId}/join { connectionId, channel? } — start receiving live.
  // 503 realtime_not_configured while Web PubSub is unprovisioned.
  join: (dealId, connectionId, channel = "general") =>
    losHttp
      .post(`/chat/${dealId}/join`, { connectionId, channel }, { noLog: true })
      .then((r) => r.data),

  // POST /chat/{dealId}/leave { connectionId, channel? }
  leave: (dealId, connectionId, channel = "general") =>
    losHttp
      .post(`/chat/${dealId}/leave`, { connectionId, channel }, { noLog: true })
      .then((r) => r.data),
};

export default loanChatApi;
